import type { LoanConfig } from './loan-math';
import type { SBALoanDrawConfig } from './SBALoanDraws';

/**
 * Single source of truth for every loan instrument Atlas tracks.
 *
 * The per-loan ledger pages (`FamilyLoanPage`, `SBALoanPage`) and the
 * cross-loan rollup (`DebtBalancesPage`) all read from here so the
 * numbers can never drift between surfaces. When these configs are
 * lifted into a Supabase `loans` table, swap the constants for a query
 * and leave the consumers untouched.
 */

/**
 * Family loan.
 *
 * Terms (per the operator):
 *   - Principal: $340,000
 *   - Effective date: May 1, 2026
 *   - First payment due: June 7, 2026 (off-cycle)
 *   - Subsequent payments due on the 1st of each month
 *   - Rate: 8.00% APR
 *   - Term: 10 years (120 monthly payments)
 */
export const FAMILY_LOAN_SLUG = 'family';

export const FAMILY_LOAN_CONFIG: LoanConfig = {
  label: 'Family loan',
  lender: 'Family',
  principalCents: 340_000_00,
  annualRate: 0.08,
  termMonths: 120,
  effectiveDate: '2026-05-01',
  firstPaymentDate: '2026-06-07',
  paymentDayOfMonth: 1,
};

/**
 * SBA loan.
 *
 * Unlike the family promissory note, the SBA loan has a 6-month draw
 * period during which the operator pulls funds against four
 * SBA-approved buckets (Equipment, Inventory, Working Capital,
 * Closing Costs). Standard amortization doesn't start until the
 * draw period closes.
 *
 * Terms (per the operator + lender closing disclosure):
 *   - Loan #: 72772-94008
 *   - Face value: $250,000 (note principal)
 *   - Borrower contribution: $28,000 (10% equity injection)
 *   - Closing date: April 24, 2026
 *   - Draw period: 6 months → ends October 24, 2026
 *   - Interest rate: 9.25% APR
 *   - Term: 10 years (120 months) total amortization
 *
 * Bucket allocations (sum to the full $250,000 face value):
 *   - Equipment:        $47,774.00
 *   - Inventory:        $62,000.00
 *   - Working Capital: $127,726.00
 *   - Closing Costs:    $12,500.00
 *
 * Closing-day flow (matches lender's closing disclosure):
 *   Sources: $250,000 note + $28,000 borrower = $278,000
 *   Uses:
 *     - $36,700 paid to others on borrower's behalf
 *       - $24,200 STEENSON ENTERPRISES (= $4,325 SBA Equipment bucket
 *                                          + $19,875 borrower contribution)
 *       - $12,500 ELOO, LLC (Working Capital draw, closing-cost reimbursement)
 *     - $8,125 prepaid finance charges (paid from borrower contribution)
 *       - $2,500 SBA Packaging Fee
 *       - $5,625 SBA Guarantee Fee
 *     - $233,175 undisbursed loan proceeds (held in line for future draws)
 *
 * Reconciling to the lender's portal balance:
 *   - At closing the loan disbursed $16,825 = $4,325 (Steenson) + $12,500 (Eloo)
 *   - Operator drew an additional $21,000 on 2026-05-26
 *   - Current loan balance: $37,825 drawn, $212,175 remaining
 */
export const SBA_LOAN_SLUG = 'sba';

export const SBA_LOAN_CONFIG: SBALoanDrawConfig = {
  label: 'SBA loan',
  loanNumber: '72772-94008',
  lender: 'SBA-backed bank',
  facePrincipalCents: 250_000_00,
  closingDate: '2026-04-24',
  drawPeriodMonths: 6,
  annualRate: 0.0925,
  termMonths: 120,
  buckets: [
    {
      key: 'equipment',
      label: 'Equipment',
      budgetCents: 47_774_00,
      description:
        'Production equipment, tooling, and capitalized fixtures.',
    },
    {
      key: 'inventory',
      label: 'Inventory',
      budgetCents: 62_000_00,
      description:
        'Raw materials and finished-goods stock — untouched until first inventory draw.',
    },
    {
      key: 'working_capital',
      label: 'Working Capital',
      budgetCents: 127_726_00,
      description:
        'Operating cash for payroll, rent, materials, and similar day-to-day expenses.',
    },
    {
      key: 'closing_costs',
      label: 'Closing Costs',
      budgetCents: 12_500_00,
      description:
        'Reserve for additional closing-related expenses. Currently untouched — SBA fees ($8,125) were paid from the borrower contribution at closing.',
    },
  ],
  draws: [
    {
      // SBA-funded slice of the $24,200 paid to Steenson at closing.
      // The remaining $19,875 came from the operator's $28k borrower
      // contribution and is documented in the closing summary card.
      id: 'closing-equipment-steenson',
      bucketKey: 'equipment',
      amountCents: 4_325_00,
      dateISO: '2026-04-24',
      note: 'Ellerman Engineering & Innovations (DBA Steenson Enterprises) — SBA-funded portion of $24,200 closing payment',
      directPay: true,
    },
    {
      // Closing-day reimbursement to the borrower's holding entity
      // for closing costs the operator paid out of pocket.
      id: 'closing-eloo-reimbursement',
      bucketKey: 'working_capital',
      amountCents: 12_500_00,
      dateISO: '2026-04-24',
      note: 'ELOO, LLC — closing-cost reimbursement to borrower',
    },
    {
      id: 'may-2026-operating-draw',
      bucketKey: 'working_capital',
      amountCents: 21_000_00,
      dateISO: '2026-05-26',
      note: 'ELOO, LLC — operating draw',
    },
  ],
  closingDisclosure: {
    borrowerContributionCents: 28_000_00,
    prepaidFeesCents: 8_125_00,
    prepaidFees: [
      {
        id: 'sba-packaging-fee',
        label: 'SBA Packaging Fee',
        amountCents: 2_500_00,
      },
      {
        id: 'sba-guarantee-fee',
        label: 'SBA Guarantee Fee',
        amountCents: 5_625_00,
      },
    ],
    closingDisbursementsCents: 36_700_00,
    closingDisbursements: [
      {
        id: 'disbursement-steenson',
        payee: 'STEENSON ENTERPRISES',
        amountCents: 24_200_00,
        note: '$4,325 from SBA Equipment bucket + $19,875 from borrower contribution',
      },
      {
        id: 'disbursement-eloo',
        payee: 'ELOO, LLC',
        amountCents: 12_500_00,
        note: 'Borrower-entity closing-cost reimbursement (Working Capital)',
      },
    ],
    undisbursedAtClosingCents: 233_175_00,
  },
};
